import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
} from '@/apis/extended/userApi';
import { CommonFormControl } from '@/components/Common/CommonFormControl';

const initialForm = {
  lenderName: '',
  lenderAddress: '',
  lenderNMLS: '',
  loanOfficerName: '',
  loanOfficerPhone: '',
  loanOfficerEmail: '',
  loanOfficerNMLS: '',
};

export const AdminLenderDefaults = () => {
  const theme = useTheme();
  const { data: user, isFetching } = useGetCurrentUserQuery();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [form, setForm] = useState({ ...initialForm });

  useEffect(() => {
    setForm({
      lenderName: user?.lenderDefaults?.lenderName || '',
      lenderAddress: user?.lenderDefaults?.lenderAddress || '',
      lenderNMLS: user?.lenderDefaults?.lenderNMLS || '',
      loanOfficerName: user?.lenderDefaults?.loanOfficerName || '',
      loanOfficerPhone: user?.lenderDefaults?.loanOfficerPhone || '',
      loanOfficerEmail: user?.lenderDefaults?.loanOfficerEmail || '',
      loanOfficerNMLS: user?.lenderDefaults?.loanOfficerNMLS || '',
    });
  }, [user]);

  const isUpdated = useMemo(() => {
    return (
      form.lenderName !== (user?.lenderDefaults?.lenderName || '') ||
      form.lenderAddress !== (user?.lenderDefaults?.lenderAddress || '') ||
      form.lenderNMLS !== (user?.lenderDefaults?.lenderNMLS || '') ||
      form.loanOfficerName !== (user?.lenderDefaults?.loanOfficerName || '') ||
      form.loanOfficerPhone !==
        (user?.lenderDefaults?.loanOfficerPhone || '') ||
      form.loanOfficerEmail !==
        (user?.lenderDefaults?.loanOfficerEmail || '') ||
      form.loanOfficerNMLS !== (user?.lenderDefaults?.loanOfficerNMLS || '')
    );
  }, [form, user]);

  const handleSave = () => {
    updateUser({
      lenderDefaults: { ...form },
    });
  };

  return (
    <Card
      sx={{
        mt: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.05)}`,
      }}
    >
      <CardHeader
        title="Lender Defaults (Auto-fill Buyer Offers)"
        titleTypographyProps={{
          variant: 'h6',
          sx: { color: 'text.primary', fontWeight: 600 },
        }}
        subheader="These values prefill lender and loan officer details."
        subheaderTypographyProps={{
          sx: { color: 'text.secondary', mt: 0.5 },
        }}
        avatar={
          <i
            className="fa-light fa-building-columns"
            style={{ color: theme.palette.primary.main, fontSize: '1.35rem' }}
            aria-hidden
          />
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Lender Name:">
              <TextField
                fullWidth
                value={form.lenderName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, lenderName: e.target.value }))
                }
              />
            </CommonFormControl>
            <CommonFormControl label="Lender Address:" sx={{ mt: 1 }}>
              <TextField
                fullWidth
                value={form.lenderAddress}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    lenderAddress: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
            <CommonFormControl label="Lender NMLS:" sx={{ mt: 1 }}>
              <TextField
                fullWidth
                value={form.lenderNMLS}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, lenderNMLS: e.target.value }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Loan Officer Name:">
              <TextField
                fullWidth
                value={form.loanOfficerName}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    loanOfficerName: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
            <CommonFormControl label="Loan Officer Phone:" sx={{ mt: 1 }}>
              <TextField
                fullWidth
                value={form.loanOfficerPhone}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    loanOfficerPhone: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
            <CommonFormControl label="Loan Officer Email:" sx={{ mt: 1 }}>
              <TextField
                fullWidth
                value={form.loanOfficerEmail}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    loanOfficerEmail: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
            <CommonFormControl label="Loan Officer NMLS:" sx={{ mt: 1 }}>
              <TextField
                fullWidth
                value={form.loanOfficerNMLS}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    loanOfficerNMLS: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
          </Grid>
        </Grid>
        <CommonFormControl sx={{ mt: 2 }}>
          <LoadingButton
            loading={isFetching || isUpdating}
            disabled={!isUpdated}
            variant="contained"
            color="primary"
            startIcon={<i className="fa-light fa-floppy-disk" />}
            onClick={handleSave}
            sx={{ fontWeight: 600, borderRadius: 2, px: 3, py: 1.25 }}
          >
            Save Lender Defaults
          </LoadingButton>
        </CommonFormControl>
      </CardContent>
    </Card>
  );
};
